/**
 * useRecorder – MediaRecorder wrapper WITH Voice Activity Detection.
 *
 * Why VAD?
 *   Without it the user has to tap twice: once to start, once to stop. With
 *   VAD, the user just taps once and speaks — we auto-stop ~1.2s after they
 *   go silent, so the conversation flows naturally.
 *
 * How the VAD works
 *   1. We route the mic stream through a Web Audio AnalyserNode.
 *   2. Every animation frame we compute the RMS (root-mean-square) amplitude
 *      of the time-domain buffer and expose it as `level` (0..1).
 *   3. State machine:
 *        - "listening_for_start" → waiting for the first frame above
 *          START_THRESHOLD. Until then, we do NOT trigger silence detection
 *          (otherwise the recorder auto-stops before the user even speaks).
 *        - "speaking"            → each frame above CONTINUE_THRESHOLD resets
 *          the silence counter.
 *        - "silence"             → frames below CONTINUE_THRESHOLD accumulate.
 *          When they exceed SILENCE_MS, we fire `onAutoStop`.
 *   4. A hard MAX_RECORDING_MS cap prevents runaway recordings.
 *
 * The user can still tap the mic button to stop manually at any time.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { ensureMicPermission } from '@/lib/nativeMic';

// Tuned empirically for a laptop/phone mic in a quiet-ish room.
const START_THRESHOLD = 0.035;    // must cross this once before VAD arms
const CONTINUE_THRESHOLD = 0.020; // below this counts as silence
const SILENCE_MS = 1200;          // how long of silence triggers auto-stop
const MIN_SPEAK_MS = 400;         // require at least this much voice before allowing auto-stop
const MAX_RECORDING_MS = 30_000;  // hard safety cap

export default function useRecorder({ onAutoStop, vad = true } = {}) {
  const [recording, setRecording] = useState(false);
  const [level, setLevel] = useState(0);        // 0..1, for UI pulse
  const [voiceDetected, setVoiceDetected] = useState(false);
  const [error, setError] = useState(null);

  // Keep latest callback in a ref so we don't rebind the audio loop each render.
  const onAutoStopRef = useRef(onAutoStop);
  useEffect(() => { onAutoStopRef.current = onAutoStop; }, [onAutoStop]);

  const chunksRef = useRef([]);
  const recorderRef = useRef(null);
  const streamRef = useRef(null);
  const stopResolverRef = useRef(null);

  const audioCtxRef = useRef(null);
  const analyserRef = useRef(null);
  const rafRef = useRef(null);

  const startedAtRef = useRef(0);
  const firstVoiceAtRef = useRef(0);
  const lastVoiceAtRef = useRef(0);
  const autoStopFiredRef = useRef(false);

  const pickMime = () => {
    const candidates = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4', ''];
    for (const c of candidates) {
      if (!c) return '';
      // eslint-disable-next-line no-undef
      if (window.MediaRecorder?.isTypeSupported(c)) return c;
    }
    return '';
  };

  /** Core VAD loop — runs every animation frame while recording. */
  const startVadLoop = useCallback((stream) => {
    // eslint-disable-next-line no-undef
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return;
    const ctx = new AC();
    audioCtxRef.current = ctx;
    const src = ctx.createMediaStreamSource(stream);
    const analyser = ctx.createAnalyser();
    analyser.fftSize = 1024;
    analyser.smoothingTimeConstant = 0.6;
    src.connect(analyser);
    analyserRef.current = analyser;

    const buf = new Float32Array(analyser.fftSize);

    const tick = () => {
      const analyserNode = analyserRef.current;
      if (!analyserNode) return;
      analyserNode.getFloatTimeDomainData(buf);
      // RMS amplitude (robust to peak spikes)
      let sum = 0;
      for (let i = 0; i < buf.length; i += 1) sum += buf[i] * buf[i];
      const rms = Math.sqrt(sum / buf.length);
      setLevel(Math.min(1, rms * 6)); // scale up for UI

      const now = performance.now();

      // Hard safety cap.
      if (now - startedAtRef.current > MAX_RECORDING_MS) {
        fireAutoStop();
        return;
      }

      if (rms >= START_THRESHOLD) {
        if (!firstVoiceAtRef.current) {
          firstVoiceAtRef.current = now;
          setVoiceDetected(true);
        }
        lastVoiceAtRef.current = now;
      } else if (rms >= CONTINUE_THRESHOLD && firstVoiceAtRef.current) {
        // Soft voice still counts as active.
        lastVoiceAtRef.current = now;
      }

      if (vad && firstVoiceAtRef.current) {
        const spokenMs = now - firstVoiceAtRef.current;
        const silenceMs = now - lastVoiceAtRef.current;
        if (spokenMs >= MIN_SPEAK_MS && silenceMs >= SILENCE_MS) {
          fireAutoStop();
          return;
        }
      }

      rafRef.current = requestAnimationFrame(tick);
    };

    const fireAutoStop = () => {
      if (autoStopFiredRef.current) return;
      autoStopFiredRef.current = true;
      onAutoStopRef.current?.();
    };

    rafRef.current = requestAnimationFrame(tick);
  }, [vad]);

  const teardownVad = useCallback(() => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = null;
    analyserRef.current = null;
    if (audioCtxRef.current) {
      try { audioCtxRef.current.close(); } catch { /* ignore */ }
      audioCtxRef.current = null;
    }
    setLevel(0);
  }, []);

  const start = useCallback(async () => {
    setError(null);
    setVoiceDetected(false);
    autoStopFiredRef.current = false;
    firstVoiceAtRef.current = 0;
    lastVoiceAtRef.current = 0;
    try {
      // On native (Capacitor), request the mic permission through the OS
      // first — otherwise the WebView's getUserMedia silently fails.
      const perm = await ensureMicPermission();
      if (perm === 'denied') {
        const err = new Error('Microphone permission denied. Open Settings to allow LingoFlow to use the microphone.');
        err.name = 'NotAllowedError';
        throw err;
      }
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
      });
      streamRef.current = stream;
      const mimeType = pickMime();
      const rec = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
      chunksRef.current = [];
      rec.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) chunksRef.current.push(e.data);
      };
      rec.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: mimeType || 'audio/webm' });
        stream.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
        teardownVad();
        if (stopResolverRef.current) {
          stopResolverRef.current(blob);
          stopResolverRef.current = null;
        }
      };
      rec.start();
      recorderRef.current = rec;
      startedAtRef.current = performance.now();
      setRecording(true);
      startVadLoop(stream);
    } catch (e) {
      setError(e?.message || 'Could not start microphone');
      setRecording(false);
      throw e;
    }
  }, [startVadLoop, teardownVad]);

  const stop = useCallback(() => {
    return new Promise((resolve) => {
      const rec = recorderRef.current;
      if (!rec || rec.state === 'inactive') {
        resolve(null);
        return;
      }
      stopResolverRef.current = resolve;
      setRecording(false);
      rec.stop();
    });
  }, []);

  const cancel = useCallback(() => {
    const rec = recorderRef.current;
    if (rec && rec.state !== 'inactive') rec.stop();
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    teardownVad();
    setRecording(false);
  }, [teardownVad]);

  // Clean up if unmounted mid-recording.
  useEffect(() => () => cancel(), [cancel]);

  return { recording, level, voiceDetected, error, start, stop, cancel };
}
